/**
 * Image Settings Component - Glass Morphism Design
 * Configuration options for image generation
 * Validates: Requirements 16.2
 */
import { useImageStore } from '../../stores/imageStore';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

interface ImageSettingsProps {
  disabled?: boolean;
}

export function ImageSettings({ disabled }: ImageSettingsProps) {
  const { settings, setSettings, selectedModel } = useImageStore();

  const isDallE3 = selectedModel === 'dall-e-3';

  return (
    <div
      className="rounded-2xl p-5 border relative overflow-hidden"
      style={{
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderColor: THEME.border,
      }}
    >
      {/* Dot pattern overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '12px 12px',
        }}
      />

      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <Icons.Settings size={16} style={{ color: THEME.accentColor }} />
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: THEME.textMain }}>
            Generation Settings
          </h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Size */}
          <div>
            <label className="block text-xs mb-2 font-medium" style={{ color: THEME.textSecondary }}>
              Size
            </label>
            <select
              value={settings.size || '1024x1024'}
              onChange={(e) => setSettings({ size: e.target.value as any })}
              disabled={disabled}
              className="w-full text-sm rounded-lg px-3 py-2 border transition-all duration-200 focus:outline-none disabled:opacity-50"
              style={{
                backgroundColor: THEME.glassLight,
                backdropFilter: 'blur(10px)',
                borderColor: THEME.border,
                color: THEME.textMain,
              }}
            >
              <option value="256x256">256×256</option>
              <option value="512x512">512×512</option>
              <option value="1024x1024">1024×1024</option>
              <option value="1024x1792">1024×1792 (Portrait)</option>
              <option value="1792x1024">1792×1024 (Landscape)</option>
            </select>
          </div>

          {/* Quality (DALL-E 3 only) */}
          <div>
            <label className="block text-xs mb-2 font-medium" style={{ color: THEME.textSecondary }}>
              Quality
            </label>
            <select
              value={settings.quality || 'standard'}
              onChange={(e) => setSettings({ quality: e.target.value as any })}
              disabled={disabled || !isDallE3}
              className="w-full text-sm rounded-lg px-3 py-2 border transition-all duration-200 focus:outline-none disabled:opacity-50"
              style={{
                backgroundColor: THEME.glassLight,
                backdropFilter: 'blur(10px)',
                borderColor: THEME.border,
                color: THEME.textMain,
              }}
            >
              <option value="standard">Standard</option>
              <option value="hd">HD</option>
            </select>
            {!isDallE3 && (
              <p className="text-xs mt-1" style={{ color: THEME.textMuted }}>
                DALL-E 3 only
              </p>
            )}
          </div>

          {/* Style (DALL-E 3 only) */}
          <div>
            <label className="block text-xs mb-2 font-medium" style={{ color: THEME.textSecondary }}>
              Style
            </label>
            <select
              value={settings.style || 'vivid'}
              onChange={(e) => setSettings({ style: e.target.value as any })}
              disabled={disabled || !isDallE3}
              className="w-full text-sm rounded-lg px-3 py-2 border transition-all duration-200 focus:outline-none disabled:opacity-50"
              style={{
                backgroundColor: THEME.glassLight,
                backdropFilter: 'blur(10px)',
                borderColor: THEME.border,
                color: THEME.textMain,
              }}
            >
              <option value="vivid">Vivid</option>
              <option value="natural">Natural</option>
            </select>
            {!isDallE3 && (
              <p className="text-xs mt-1" style={{ color: THEME.textMuted }}>
                DALL-E 3 only
              </p>
            )}
          </div>

          {/* Number of Images (DALL-E 2 only for n>1) */}
          <div>
            <label className="block text-xs mb-2 font-medium" style={{ color: THEME.textSecondary }}>
              Count
            </label>
            <select
              value={settings.n || 1}
              onChange={(e) => setSettings({ n: parseInt(e.target.value) })}
              disabled={disabled || isDallE3}
              className="w-full text-sm rounded-lg px-3 py-2 border transition-all duration-200 focus:outline-none disabled:opacity-50"
              style={{
                backgroundColor: THEME.glassLight,
                backdropFilter: 'blur(10px)',
                borderColor: THEME.border,
                color: THEME.textMain,
              }}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <option key={n} value={n}>
                  {n} image{n > 1 ? 's' : ''}
                </option>
              ))}
            </select>
            {isDallE3 && (
              <p className="text-xs mt-1" style={{ color: THEME.textMuted }}>
                DALL-E 3: 1 only
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
